import React, { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { usePostHog } from '@/hooks/usePostHog';

const BookDemo = () => {
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { posthog } = usePostHog();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isFormLoaded) {
          loadHubSpotForm();
          setIsFormLoaded(true);
        }
      },
      {
        threshold: 0.1,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isFormLoaded]);

  const loadHubSpotForm = () => {
    const existingScript = document.querySelector('script[src="//js.hsforms.net/forms/embed/v2.js"]');
    
    const createForm = () => {
      if ((window as any).hbspt) {
        (window as any).hbspt.forms.create({
          portalId: "99128",
          formId: "ea664590-de56-45ab-9719-4acf6b81ccc7",
          region: "na1",
          target: "#hubspot-demo-form-page",
        });
        
        posthog?.capture('demo_form_loaded', {
          source: 'hubspot_form_page',
          page: '/book-demo',
        });
      }
    };

    if (existingScript && (window as any).hbspt) {
      createForm();
    } else {
      const script = document.createElement('script');
      script.charset = 'utf-8';
      script.type = 'text/javascript';
      script.src = '//js.hsforms.net/forms/embed/v2.js';
      script.async = true;
      script.onload = () => createForm();
      document.head.appendChild(script);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-20">
        <section 
          ref={sectionRef}
          className="py-16 px-4"
        >
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Left Column - Messaging */}
              <div className="space-y-8">
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    Highest Rated Employee Recognition Platform
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                    See Why 1000+ Companies Choose 
                    <span className="block text-blue-600">Assembly</span>
                  </h1>
                  
                  <p className="text-xl text-gray-600 leading-relaxed">
                    Book a personalized demo and discover how Assembly can transform your workplace culture, boost engagement, and drive measurable results.
                  </p>
                </div>

                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">What you'll see in your demo:</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Live Platform Walkthrough</h4>
                        <p className="text-gray-600">See exactly how Assembly works with your team size and industry</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">ROI Calculator & Case Studies</h4>
                        <p className="text-gray-600">Discover potential cost savings and engagement improvements</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Custom Implementation Plan</h4>
                        <p className="text-gray-600">Get a tailored roadmap for rolling out to your organization</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Integration Demo</h4>
                        <p className="text-gray-600">See how Assembly connects with your existing tools</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <img src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/6753dfb7e5c867c416c641d4_03.avif" alt="Shannon Townsend" className="w-8 h-8 rounded-full border-2 border-white" />
                      <img src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/6759422714e35bd9e0c99117_Coca-Cola.svg" alt="Coca-Cola" className="h-6 w-auto" />
                    </div>
                    <div className="flex items-center gap-1 ml-auto">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-4 h-4 text-yellow-400 fill-current">⭐</div>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-700 italic">
                    "With all the complications and disruptions to our daily routines... Assembly was a key part of continuing employee engagement throughout these difficult times. We are approaching the end of our first year with Assembly and looking forward to continuing the partnership...."
                  </p>
                  <p className="text-sm text-gray-600 mt-2 font-semibold">Shannon Townsend, Sales Execution Manager at Coca-Cola</p>
                </div>
              </div>

              {/* Right Column - HubSpot Form */}
              <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Book Your Demo
                  </h3>
                  <p className="text-gray-600">
                    Fill out the form and we'll be in touch shortly
                  </p>
                </div>
                
                <div 
                  id="hubspot-demo-form-page" 
                  className="min-h-[400px]"
                >
                  {!isFormLoaded && (
                    <div className="text-center space-y-4 py-12">
                      <div className="w-12 h-12 mx-auto border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-gray-500">Loading form...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BookDemo;
